import { useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  nextStep,
  prevStep,
  setNavigating,
  setDirection,
  setIsLoading,
  stopTutorial,
} from "../../Redux/tutorialSlice";
import { useLocation, useNavigate } from "react-router-dom";
import tutorialSteps from "./tutorialSteps";
import { setWizardStep, openWizard } from "../../Redux/wizardSlice";

const useTutorialSteps = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentStep = useSelector((state) => state.tutorial.currentStep);
  const navigating = useSelector((state) => state.tutorial.navigating);
  const location = useLocation();

  const navigationLock = useRef(false);

  const waitForCrepContent = (selector) =>
    new Promise((resolve) => {
      const existing = document.querySelector(selector);
      if (existing) return resolve(true);

      const observer = new MutationObserver(() => {
        const found = document.querySelector(selector);
        if (found) {
          observer.disconnect();
          resolve(true);
        }
      });

      observer.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => {
        observer.disconnect();
        resolve(false);
      }, 1500);
    });

  const maybeNavigate = (stepData) => {
    if (stepData.route !== tutorialSteps[currentStep]?.route) {
      dispatch(setNavigating(true));
      navigate(stepData.route);
    }
  };

  const syncWizardStep = (wizardStep) => {
    if (typeof wizardStep === "number") {
      dispatch(openWizard());
      dispatch(setWizardStep(wizardStep));
    }
  };

  const finalizeStep = (stepIndex) => {
    dispatch(stepIndex > currentStep ? nextStep() : prevStep());
    dispatch(setNavigating(false));
    dispatch(setIsLoading(false));

    setTimeout(() => {
      navigationLock.current = false;
    }, 300);
  };

  const processStep = useCallback(
    async (stepIndex) => {
      if (navigationLock.current) return;
      if (stepIndex < 0 || stepIndex >= tutorialSteps.length) return;

      const stepData = tutorialSteps[stepIndex];
      if (!stepData) return;

      navigationLock.current = true;
      maybeNavigate(stepData);
      syncWizardStep(stepData.wizardStep);
      finalizeStep(stepIndex);
    },
    [navigate, dispatch, currentStep]
  );

  const handleNextStep = useCallback(
    (event) => {
      event?.preventDefault();
      event?.stopPropagation();

      if (navigationLock.current || navigating) return;

      const nextStepIndex = currentStep + 1;
      if (nextStepIndex >= tutorialSteps.length) {
        dispatch(stopTutorial());
        return;
      }

      dispatch(setDirection("next"));
      dispatch(setIsLoading(true));
      processStep(nextStepIndex);
    },
    [currentStep, navigating, processStep]
  );

  const handlePrevStep = useCallback(
    (event) => {
      event?.preventDefault();
      event?.stopPropagation();

      if (navigationLock.current || navigating) return;

      const targetStep = currentStep - 1;
      if (targetStep < 0) return;

      dispatch(setDirection("prev"));
      dispatch(setIsLoading(true));

      if (currentStep === 12 && targetStep === 11) {
        dispatch(openWizard());
      }

      processStep(targetStep);
    },
    [currentStep, navigating, processStep]
  );

  return { processStep, handleNextStep, handlePrevStep };
};

export default useTutorialSteps;
